import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '~spa/Components/Breadcrumbs/Breadcrumbs';
import TitleBar from '~spa/Components/TitleBar/TitleBar';
import UnescapedTrans from './UnescapedTrans';
import { buildSeoNav, getStaticPageBreadcrumbsTranslationKey } from './utils';
import { isUserLoggedIn } from '~shared/utils/user';

import {
    INPLAY_SLUG,
    HORSE_RACING_SLUG,
    GREYHOUND_RACING_SLUG,
    TROTTING_SLUG,
    COUPONS_SLUG,
    VIRTUAL_SLUG,
    MOTORSPORT_SLUG,
} from '~shared/utils/sportsbook';

const SeoNavigation = ({ locationState }) => {
    const [ t ] = useTranslation();

    const {
        context,
        contextId,
    } = locationState;

    const {
        systemUrlPath,
        error,
        sportSlug,
        sportName,
    } = context;

    const [ breadcrumbs, setBreadcrumbs ] = useState(null);
    const [ titleBar, setTitleBar ] = useState(null);

    const getPageData = (systemUrlPath) => {
        const [, firstSystemUrlToken, secondSystemUrlToken] = systemUrlPath.split('/');

        const isInplay = firstSystemUrlToken === INPLAY_SLUG;
        const isVirtuals = firstSystemUrlToken === VIRTUAL_SLUG;
        const isCoupons = secondSystemUrlToken === COUPONS_SLUG;
        const isMotorSport = firstSystemUrlToken === MOTORSPORT_SLUG;
        const isRacing = [
            HORSE_RACING_SLUG,
            GREYHOUND_RACING_SLUG,
            TROTTING_SLUG,
        ].includes(sportSlug);

        let pageName = null;

        if (isInplay) {
            pageName = t('breadcrumbs.inplay');
        } else if (isCoupons) {
            pageName = t('breadcrumbs.coupons');
        } else if (isRacing || isVirtuals || isMotorSport) {
            pageName = sportName;
        } else {
            const translationKey = getStaticPageBreadcrumbsTranslationKey(
                firstSystemUrlToken
            );

            if (translationKey) {
                pageName = t(translationKey);
            }
        }

        return {
            pageName,
            isInplay,
            isRacing,
            isCoupons,
            isVirtuals,
            isMotorSport,
        };
    };

    useEffect(() => {
        /**
         * Fetches translated breadcrumbs data
         * @returns {void}
         */
        async function fetchData() {
            const data = {
                ...context,
                ...getPageData(systemUrlPath),
            };
            const { breadcrumbsList, titleBar } = await buildSeoNav(contextId, data);
            setBreadcrumbs(breadcrumbsList);
            setTitleBar(titleBar);
        }

        fetchData();
    }, [
        systemUrlPath,
    ]);

    const MemoizedTitleBar = ({ titleBar }) => {
        const component = useMemo(() => {
            if (!titleBar) {
                return null;
            }

            return (
                <TitleBar isLoggedIn={isUserLoggedIn()}>
                    <UnescapedTrans i18nKey={titleBar.translationTemplateKey}>
                        {{ ...titleBar.params }}
                    </UnescapedTrans>
                </TitleBar>
            );
        }, [
            titleBar,
        ]);

        return component;
    };

    const MemoizedBreadcrumbs = ({ breadcrumbs }) => {
        const component = useMemo(() => {
            if (!breadcrumbs) {
                return null;
            }

            return (
                <Breadcrumbs data={breadcrumbs} />
            );
        }, [
            breadcrumbs,
        ]);

        return component;
    };

    if (systemUrlPath === null || error === true) {
        return null;
    }

    return (
        <>
            <MemoizedTitleBar titleBar={titleBar} />
            <MemoizedBreadcrumbs breadcrumbs={breadcrumbs} />
        </>
    );
};

SeoNavigation.propTypes = {
    locationState: PropTypes.object.isRequired,
};

export default SeoNavigation;
